import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ConfigProvider, Layout, Menu, Typography, Button, Spin, theme } from 'antd';
import {
  DashboardOutlined,
  FileSearchOutlined,
  ExperimentOutlined,
  BulbOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import Dashboard from './pages/Dashboard';
import Cases from './pages/Cases';
import CaseDetail from './pages/CaseDetail';
import Pathology from './pages/Pathology';
import Insights from './pages/Insights';
import Login from './pages/Login';
import { BranchProvider } from './components/BranchContext';
import BranchFilter from './components/BranchFilter';

const { Sider, Content, Header } = Layout;
const { Title, Text } = Typography;

const SIDEBAR_BG = '#1A3C7A';
const FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const menuItems = [
  { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/cases', icon: <FileSearchOutlined />, label: 'Cases' },
  { key: '/pathology', icon: <ExperimentOutlined />, label: 'Pathology' },
  { key: '/insights', icon: <BulbOutlined />, label: 'Insights' },
];

const PAGE_TITLES = {
  '/': 'Dashboard Overview',
  '/cases': 'Audit Cases',
  '/pathology': 'Pathology Accuracy',
  '/insights': 'AI Insights',
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(undefined); // undefined = loading, null = not authed, object = authed

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return unsub;
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
  };

  const selectedKey = () => {
    if (location.pathname.startsWith('/cases/')) return '/cases';
    if (location.pathname.startsWith('/cases')) return '/cases';
    if (location.pathname.startsWith('/pathology')) return '/pathology';
    if (location.pathname.startsWith('/insights')) return '/insights';
    return '/';
  };

  const pageTitle = location.pathname.startsWith('/cases/')
    ? 'Case Detail'
    : PAGE_TITLES[location.pathname] || 'Dashboard';

  // Loading state while Firebase checks auth
  if (user === undefined) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Not authenticated — show login
  if (!user) {
    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1A3C7A',
            borderRadius: 12,
            fontFamily: FONT,
            controlOutline: 'transparent',
            controlOutlineWidth: 0,
          },
          components: {
            Input: {
              activeBorderColor: '#333',
              hoverBorderColor: '#666',
            },
          },
        }}
      >
        <Login />
      </ConfigProvider>
    );
  }

  // Authenticated — show dashboard
  return (
    <BranchProvider>
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1A3C7A',
          borderRadius: 12,
          colorBgContainer: '#ffffff',
          fontFamily: FONT,
        },
        algorithm: theme.defaultAlgorithm,
      }}
    >
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          width={220}
          theme="dark"
          breakpoint="lg"
          collapsedWidth={60}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            background: SIDEBAR_BG,
          }}
        >
          <div style={{ padding: '16px 12px', textAlign: 'center' }}>
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12,
              background: '#fff', borderRadius: 12, padding: '10px 14px', margin: '0 8px 10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}>
              <img src="/yashoda-logo.png" alt="Yashoda" style={{ height: 28, objectFit: 'contain' }} />
              <div style={{ width: 1, height: 24, background: '#d9d9d9' }} />
              <img src="/5c-logo.png" alt="5C Network" style={{ height: 28, objectFit: 'contain' }} />
            </div>
            <Title level={5} style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: 12, letterSpacing: 0.5 }}>
              Report Audit Dashboard
            </Title>
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey()]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{ background: 'transparent', borderRight: 'none' }}
          />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, display: 'block', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.email}
            </Text>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              size="small"
              style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, padding: '0 4px' }}
            >
              Sign out
            </Button>
          </div>
        </Sider>
        <Layout style={{ marginLeft: 220 }}>
          <Header
            style={{
              background: '#fff',
              padding: '0 24px',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: 48,
            }}
          >
            <Title level={5} style={{ margin: 0, color: '#1A3C7A' }}>
              {pageTitle}
            </Title>
            <BranchFilter />
          </Header>
          <Content
            style={{
              margin: 16,
              padding: 16,
              background: '#f5f5f5',
              minHeight: 'calc(100vh - 80px)',
            }}
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/cases" element={<Cases />} />
              <Route path="/cases/:studyFk" element={<CaseDetail />} />
              <Route path="/pathology" element={<Pathology />} />
              <Route path="/insights" element={<Insights />} />
            </Routes>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
    </BranchProvider>
  );
};

export default App;
